import {html} from 'lit';
import {ref} from 'lit/directives/ref.js';
import dayjs from 'dayjs';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
  chainCmd,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import {CAMPAIGN_TYPE} from '@gl/lit/types/audience_exports.js';
import {
  AudienceFrequencyEnum,
  AudienceScheduleEndTypeEnum,
  AudienceScheduleStartTypeEnum,
} from '@gl/lit/types/journey/audience_frequency.js';
import {
  dispatchChangeEvent,
  getDayInfo,
  getDefaultEndDate,
  getDefaultOneTimeState,
  getDefaultOngoingState,
  getDefaultStartDate,
  normalizeFrequency,
} from './utils.js';
import type {
  DateInputString,
  HourAndMinuteString,
  ISOString,
  Nullable,
} from '@gl/lit/types/general.js';
import type {Timezone} from '@gl/lit/types/timezone.js';
import type {AudienceScheduleOccurrence} from '@gl/lit/types/journey/schedule.js';
import {ByType} from '@gl/lit/types/journey/schedule.js';
import {
  convertToDateInputValue,
  convertToTimeInputValue,
} from '@gl/lit/utils/dates.js';
import {
  getBrowserTimezone,
  getTimezone,
} from '@gl/lit/common/constants/timezones.js';
import type {WeekdaysEnum} from '@gl/lit/common/constants/dates.js';
import type {ChangeWeekdayEvent} from '@gl/lit/components/base/weekday_selector/utils.js';
import type FlyAudienceExportSchedule from './index.js';
import {SelectVariant} from '@gl/lit/components/base/select/index.js';
import {checkEquality} from '@gl/lit/utils/arrays.js';
import {titleize} from '@gl/lit/utils/strings.js';

// -- Component dependencies
import '@gl/lit/components/base/list_item/index.js';
import '@gl/lit/components/base/select/index.js';
import '@gl/lit/components/base/schedule/frequency/index.js';
import '@gl/lit/components/base/schedule/date/index.js';
import '@gl/lit/components/base/weekday_selector/index.js';

export type Model = {
  disabled: boolean;
  exportedAudience: boolean;
  campaignType: CAMPAIGN_TYPE;
  frequency: Nullable<AudienceFrequencyEnum>;
  startType: AudienceScheduleStartTypeEnum;
  startDate: Nullable<DateInputString>;
  startTime: Nullable<HourAndMinuteString>;
  startTimezone: Nullable<Timezone>;
  endType: Nullable<AudienceScheduleEndTypeEnum>;
  endDate: Nullable<DateInputString>;
  endTime: Nullable<HourAndMinuteString>;
  endTimezone: Nullable<Timezone>;
  occurrence: Nullable<AudienceScheduleOccurrence[]>;
};

type StartScheduleState = {
  [K in keyof Model as K extends `start${string}` ? K : never]: Model[K];
};

type EndScheduleState = {
  [K in keyof Model as K extends `end${string}` ? K : never]: Model[K];
};

// export type CModel = ComputedModel<Model>;

export const Msg = makeVariants({
  setDisabled: Variant<boolean>()('set-disabled'),
  setCampaignType: Variant<CAMPAIGN_TYPE>()('set-campaign-type'),
  setFrequency: Variant<Nullable<AudienceFrequencyEnum>>()('set-frequency'),
  setStartState: Variant<StartScheduleState>()('set-start-state'),
  setStartType: Variant<AudienceScheduleStartTypeEnum>()('set-start-type'),
  setStartDate: Variant<DateInputString | ''>()('set-start-date'),
  setStartTime: Variant<HourAndMinuteString | ''>()('set-start-time'),
  setStartTimezone: Variant<string>()('set-start-timezone'),
  setEndState: Variant<EndScheduleState>()('set-end-state'),
  setEndType: Variant<AudienceScheduleEndTypeEnum | ''>()('set-end-type'),
  setEndDate: Variant<DateInputString | ''>()('set-end-date'),
  setEndTime: Variant<HourAndMinuteString | ''>()('set-end-time'),
  setEndTimezone: Variant<string>()('set-end-timezone'),
  setOccurrence: Variant<AudienceScheduleOccurrence[]>()('set-occurrence'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(props: InitialProperties, _cmd: Cmd, _model?: Model) {
  const disabled = <boolean | undefined>props.get('disabled') ?? false;
  const exportedAudience =
    <boolean | undefined>props.get('exportedAudience') ?? false;
  const campaignType = <CAMPAIGN_TYPE>props.get('campaignType');
  const frequency = <AudienceFrequencyEnum | ''>props.get('frequency');
  const startType = <AudienceScheduleStartTypeEnum>props.get('startType');
  const startISOTime = <ISOString | ''>props.get('startDate') ?? '';
  const startTimezone = <string>props.get('startTimezone');
  const endType = <AudienceScheduleEndTypeEnum | ''>props.get('endType');
  const endISOTime = <ISOString | ''>props.get('endDate') ?? '';
  const endTimezone = <string>props.get('endTimezone');
  const occurrence =
    <Nullable<AudienceScheduleOccurrence[]>>props.get('occurrence') ?? [];

  const startDate = startISOTime
    ? convertToDateInputValue(startISOTime) || null
    : null;
  const startTime = startISOTime
    ? convertToTimeInputValue(startISOTime) || null
    : null;
  const endDate = endISOTime
    ? convertToDateInputValue(endISOTime) || null
    : null;
  const endTime = endISOTime
    ? convertToTimeInputValue(endISOTime) || null
    : null;

  const updatedModel = {
    disabled,
    exportedAudience,
    campaignType,
    frequency: frequency !== '' ? frequency : null,
    startType,
    startDate,
    startTime,
    startTimezone: getTimezone(startTimezone) ?? getBrowserTimezone(),
    endType: endType !== '' ? endType : null,
    endDate,
    endTime,
    endTimezone: getTimezone(endTimezone) ?? getBrowserTimezone(),
    occurrence,
  };

  return updatedModel;
}

function initializeMonthlyValues(host: FlyAudienceExportSchedule) {
  host.$monthlyOptionsSelect.value?.initialize();
}

// -- Update

// eslint-disable-next-line complexity
function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-disabled':
      return {...model, disabled: msg.value};

    case 'set-campaign-type': {
      if (model.campaignType === msg.value) return model;
      const isSingleExport = msg.value === CAMPAIGN_TYPE.ONE_TIME;
      const updatedSchedule = isSingleExport
        ? getDefaultOneTimeState(model)
        : getDefaultOngoingState(model);

      return [
        {...model, ...updatedSchedule, campaignType: msg.value},
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-frequency': {
      if (model.frequency === msg.value) return model;
      const occurrence =
        model.frequency === AudienceFrequencyEnum.MONTHLY
          ? [{type: ByType.WEEK_DAY, values: [0, 1, 2, 3, 4, 5, 6]}]
          : model.occurrence;
      return [
        {...model, occurrence, frequency: msg.value},
        cmd((host: FlyAudienceExportSchedule) => {
          dispatchChangeEvent(host);
          if (host.$frequency) {
            host.$frequency.$select?.$options.forEach(item => {
              if (item.value === (msg.value ?? '')) {
                // host.$frequency?.$select?.updateOption(item.value, true);
              }
            });
          }
        }),
      ];
    }

    case 'set-start-state': {
      return [{...model, ...msg.value}, cmd(dispatchChangeEvent)];
    }

    case 'set-start-type': {
      if (model.startType === msg.value) return model;

      if (msg.value === AudienceScheduleStartTypeEnum.NOW) {
        return [
          {
            ...model,
            startType: msg.value,
            startDate: null,
            startTime: null,
            startTimezone: null,
          },
          cmd(dispatchChangeEvent),
        ];
      }

      const start = getDefaultStartDate();
      return [
        {
          ...model,
          startType: msg.value,
          startDate: convertToDateInputValue(start) || null,
          startTime: convertToTimeInputValue(start) || null,
          startTimezone: model.endTimezone ?? getBrowserTimezone(),
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-start-date': {
      if (model.startDate === msg.value) return model;
      return [
        {
          ...model,
          startDate: msg.value || null,
        },
        cmd(chainCmd(initializeMonthlyValues, dispatchChangeEvent)),
      ];
    }

    case 'set-start-time': {
      if (model.startTime === msg.value) return model;
      return [
        {
          ...model,
          startTime: msg.value || null,
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-start-timezone': {
      if (model.startTimezone?.name === msg.value) return model;
      if (
        msg.value === '' &&
        model.startTimezone?.name === getBrowserTimezone().name
      ) {
        return model;
      }
      const startTimezone = getTimezone(msg.value) ?? getBrowserTimezone();
      if (model.startTimezone === null && msg.value === null) return model;
      if (model.startTimezone?.equals(startTimezone)) return model;
      return [
        {
          ...model,
          startTimezone,
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-end-state':
      return [{...model, ...msg.value}, cmd(dispatchChangeEvent)];

    case 'set-end-type': {
      if (model.endType === msg.value) return model;

      if (msg.value === '' || msg.value === AudienceScheduleEndTypeEnum.NEVER) {
        return [
          {
            ...model,
            endType: msg.value !== '' ? msg.value : null,
            endDate: null,
            endTime: null,
            endTimezone: null,
          },
          cmd(dispatchChangeEvent),
        ];
      }

      const end = getDefaultEndDate(model.startDate, model.startTime);
      return [
        {
          ...model,
          endType: msg.value,
          endDate: convertToDateInputValue(end) || null,
          endTime: convertToTimeInputValue(end) || null,
          endTimezone: model.endTimezone ?? getBrowserTimezone(),
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-end-date': {
      if (model.endDate === msg.value) return model;
      return [
        {
          ...model,
          endDate: msg.value || null,
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-end-time': {
      if (model.endTime === msg.value) return model;
      return [
        {
          ...model,
          endTime: msg.value || null,
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-end-timezone': {
      const nameEqualsValue = model.endTimezone?.name === msg.value;
      const msgIsEmpty = msg.value === '';
      const nameMatchesBrowserTimezone =
        model.endTimezone?.name === getBrowserTimezone().name;

      if (nameEqualsValue) return model;
      if (msgIsEmpty && nameMatchesBrowserTimezone) return model;
      const endTimezone = getTimezone(msg.value) ?? getBrowserTimezone();
      if (model.endTimezone === null && msg.value === null) return model;
      if (model.endTimezone?.equals(endTimezone)) return model;
      return [
        {
          ...model,
          endTimezone,
        },
        cmd(dispatchChangeEvent),
      ];
    }

    case 'set-occurrence':
      if (
        model.occurrence &&
        model.occurrence.length === msg.value.length &&
        checkEquality(
          model.occurrence,
          msg.value,
          (a, b) =>
            a.type === b.type &&
            checkEquality(a.values, b.values) &&
            checkEquality(a.bySetPos ?? [], b.bySetPos ?? [])
        )
      ) {
        return model;
      }
      return [
        {
          ...model,
          occurrence: [...msg.value],
        },
        cmd(dispatchChangeEvent),
      ];
  }
}
// eslint-enable-next-line complexity

// -- View

function view(
  this: FlyAudienceExportSchedule,
  model: Model,
  handleUpdate: UpdateHandler<Model, Msg>
) {
  const isSingleExport = model.campaignType === CAMPAIGN_TYPE.ONE_TIME;
  const isStartInFuture =
    model.startType === AudienceScheduleStartTypeEnum.SCHEDULE &&
    dayjs
      .tz(model.startDate, model.startTimezone?.name)
      .isAfter(dayjs.tz(dayjs(), model.startTimezone?.name).add(1, 'day'));

  return html`
    <div class="config__section config__section--frequency">
      <fly-schedule-frequency
        ?hidden=${isSingleExport}
        ?disabled=${isSingleExport || model.disabled}
        default-value=${AudienceFrequencyEnum.DAILY}
        value=${normalizeFrequency(model.frequency ?? '') ?? ('' as const)}
        @frequency:change=${(
          event: CustomEvent<{value: AudienceFrequencyEnum}>
        ) => {
          handleUpdate(Msg.setFrequency(event.detail.value));
        }}
      ></fly-schedule-frequency>
      ${renderMonthlyOptionDropdown(this)}
    </div>

    <div class="config__section">
      <fly-schedule-date
        label="Start"
        no-schedule-label="Now"
        ?scheduled=${model.startType === AudienceScheduleStartTypeEnum.SCHEDULE}
        date=${model.startDate ?? ''}
        time=${model.startTime ?? ''}
        timezone=${model.startTimezone?.name ?? ''}
        ?disabled=${model.disabled ||
        (model.exportedAudience && !isStartInFuture)}
        @scheduledate:change:scheduled=${(
          event: CustomEvent<{value: boolean}>
        ) => {
          handleUpdate(
            Msg.setStartType(
              event.detail.value
                ? AudienceScheduleStartTypeEnum.SCHEDULE
                : AudienceScheduleStartTypeEnum.NOW
            )
          );
        }}
        @scheduledate:change:date=${(
          event: CustomEvent<{value: DateInputString | ''}>
        ) => {
          handleUpdate(Msg.setStartDate(event.detail.value ?? ''));
        }}
        @scheduledate:change:time=${(
          event: CustomEvent<{value: HourAndMinuteString | ''}>
        ) => {
          handleUpdate(Msg.setStartTime(event.detail.value));
        }}
        @scheduledate:change:timezone=${(
          event: CustomEvent<{value: Timezone}>
        ) => {
          handleUpdate(Msg.setStartTimezone(event.detail?.value?.name ?? ''));
        }}
      >
      </fly-schedule-date>
    </div>

    <div class="config__section">
      <fly-schedule-date
        label="End"
        ?hidden=${isSingleExport}
        ?disabled=${isSingleExport || model.disabled}
        no-schedule-label="Never"
        ?scheduled=${model.endType === AudienceScheduleEndTypeEnum.SCHEDULE}
        date=${model.endDate ?? ''}
        time=${model.endTime ?? ''}
        timezone=${model.endTimezone?.name ?? ''}
        @scheduledate:change:scheduled=${(
          event: CustomEvent<{value: boolean}>
        ) => {
          handleUpdate(
            Msg.setEndType(
              event.detail.value
                ? AudienceScheduleEndTypeEnum.SCHEDULE
                : AudienceScheduleEndTypeEnum.NEVER
            )
          );
        }}
        @scheduledate:change:date=${(
          event: CustomEvent<{value: DateInputString | ''}>
        ) => {
          handleUpdate(Msg.setEndDate(event.detail.value ?? ''));
        }}
        @scheduledate:change:time=${(
          event: CustomEvent<{value: HourAndMinuteString | ''}>
        ) => {
          handleUpdate(Msg.setEndTime(event.detail.value));
        }}
        @scheduledate:change:timezone=${(
          event: CustomEvent<{value: Timezone}>
        ) => {
          handleUpdate(Msg.setEndTimezone(event.detail?.value?.name ?? ''));
        }}
      >
      </fly-schedule-date>
    </div>

    ${renderWeekdaySelector(this)}
  `;
}

function renderMonthlyOptionDropdown(host: FlyAudienceExportSchedule) {
  if (
    host.model.frequency !== AudienceFrequencyEnum.MONTHLY ||
    !host.session?.enabled('additionalSchedulingOptions')
  ) {
    return html``;
  }
  const dayInfo = getDayInfo(host.model.startDate);
  return html`
    <fly-select
      ${ref(host.$monthlyOptionsSelect)}
      selectionRequired
      variant="${SelectVariant.CONDENSED_ROUNDED_SQUARE}"
      .label=${`Monthly on day ${dayInfo.dayOfMonth}`}
      .initial=${`Monthly on day ${dayInfo.dayOfMonth}`}
    >
      <fly-list-item
        value=${`Monthly on day ${dayInfo.dayOfMonth}`}
        @selected=${() => {
          host.handleUpdate(
            Msg.setOccurrence([
              {
                type: ByType.MONTH_DAY,
                values: [dayInfo.dayOfMonth],
                bySetPos: null,
              },
            ])
          );
        }}
      >
        ${`Monthly on day ${dayInfo.dayOfMonth}`}
      </fly-list-item>
      <fly-list-item
        value=${`${dayInfo.occurrenceSuffix} ${dayInfo.weekday} of each month`}
        @selected=${() => {
          host.handleUpdate(
            Msg.setOccurrence([
              {
                type: ByType.WEEK_DAY,
                values: [dayInfo.day as WeekdaysEnum],
                bySetPos: [dayInfo.occurrence],
              },
            ])
          );
        }}
      >
        ${`${dayInfo.occurrenceSuffix} ${titleize(
          dayInfo.weekday
        )} of each month`}
      </fly-list-item>
    </fly-select>
  `;
}

function renderWeekdaySelector(host: FlyAudienceExportSchedule) {
  const isSingleExport = host.model.campaignType === CAMPAIGN_TYPE.ONE_TIME;
  if (
    !isSingleExport &&
    host.model.frequency !== AudienceFrequencyEnum.MONTHLY &&
    host.model.frequency !== AudienceFrequencyEnum.WEEKLY
  ) {
    return html`
      <div>
        <label>Select days of the week</label>
        <br />
        <div class="weekdays">
          <fly-weekday-selector
            ?disabled=${host.model.disabled}
            multiSelect
            .value=${(host.model.occurrence ?? [])
              .filter(({type}) => type === ByType.WEEK_DAY)
              .flatMap(o => o.values)}
            @change:weekday=${(event: ChangeWeekdayEvent) => {
              host.handleUpdate(
                Msg.setOccurrence([
                  {
                    type: ByType.WEEK_DAY,
                    values: event.detail.values,
                    bySetPos: null,
                  },
                ])
              );
            }}
          ></fly-weekday-selector>
        </div>
      </div>
    `;
  }
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
